import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AwsProvider, useAws } from "@/context/AwsContext";
import AwsGuidedPanel from "@/components/aws/AwsGuidedPanel";
import Ec2Detail from "@/components/aws/Ec2Detail";
import LaunchWizard from "@/components/aws/LaunchWizard";
import { IamUserDetail, IamRoleDetail } from "@/components/aws/IamDetail";
import {
  LoadBalancersView, AutoScalingView, RdsView, ElastiCacheView, Route53View,
  CloudFrontView, WafView, KmsView, SecretsView, ConfigView,
} from "@/components/aws/AwsResources";
import AwsTopology from "@/components/aws/AwsTopology";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Server, Network, Shield, Database, Users, ScrollText, Activity, DollarSign,
  TerminalSquare, Home, ChevronDown, Boxes, GraduationCap, Cloud, Play, Square, RotateCw,
  Trash2, Plus, Globe, Lock, HardDrive, X, ArrowLeft, Cpu, CircleDot,
  Scale, TrendingUp, MemoryStick, Globe2, ShieldCheck, KeyRound, ClipboardCheck, Waypoints,
} from "lucide-react";

const ORANGE = "#ff9900";
const SERVICES = [
  { id: "home", label: "Console Home", icon: Home },
  { id: "topology", label: "Resource Topology", icon: Waypoints, cat: "Overview" },
  { id: "ec2", label: "EC2", icon: Server, cat: "Compute" },
  { id: "load-balancers", label: "Load Balancers", icon: Scale, cat: "Compute" },
  { id: "auto-scaling", label: "Auto Scaling", icon: TrendingUp, cat: "Compute" },
  { id: "vpc", label: "VPC", icon: Network, cat: "Networking" },
  { id: "security-groups", label: "Security Groups", icon: Shield, cat: "Networking" },
  { id: "route53", label: "Route 53", icon: Globe2, cat: "Networking" },
  { id: "cloudfront", label: "CloudFront", icon: Cloud, cat: "Networking" },
  { id: "rds", label: "RDS", icon: Database, cat: "Database" },
  { id: "cache", label: "ElastiCache", icon: MemoryStick, cat: "Database" },
  { id: "s3", label: "S3", icon: Boxes, cat: "Storage" },
  { id: "iam", label: "IAM", icon: Users, cat: "Security" },
  { id: "waf", label: "WAF & Shield", icon: ShieldCheck, cat: "Security" },
  { id: "kms", label: "KMS", icon: KeyRound, cat: "Security" },
  { id: "secrets", label: "Secrets Manager", icon: Lock, cat: "Security" },
  { id: "cloudwatch", label: "CloudWatch", icon: Activity, cat: "Management" },
  { id: "cloudtrail", label: "CloudTrail", icon: ScrollText, cat: "Management" },
  { id: "config", label: "AWS Config", icon: ClipboardCheck, cat: "Management" },
  { id: "cost", label: "Billing & Cost", icon: DollarSign, cat: "Cloud Financial Mgmt" },
  { id: "cloudshell", label: "CloudShell", icon: TerminalSquare, cat: "Tools" },
];

const STATE_COLOR = {
  running: "text-emerald-400", available: "text-emerald-400", "in-use": "text-emerald-400",
  stopped: "text-slate-400", pending: "text-amber-400", terminated: "text-rose-400",
  attached: "text-emerald-400", detached: "text-slate-400",
};

function StatusDot({ state }) {
  return <span className={`inline-flex items-center gap-1.5 ${STATE_COLOR[state] || "text-slate-300"}`}>
    <CircleDot className="h-3 w-3" /> {state}</span>;
}

function Th({ children }) { return <th className="text-left font-semibold px-3 py-2 text-slate-300 whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${className}`}>{children}</td>; }

function Empty({ label }) {
  return <div className="text-center py-14 text-slate-400 text-sm border border-dashed border-[#414750] rounded-lg">{label}</div>;
}

// ---------------- Console Home ----------------
function ConsoleHome({ setView }) {
  const { state, byType, region } = useAws();
  const counts = {
    EC2: byType("instance").length, VPC: byType("vpc").length,
    "S3 buckets": byType("bucket").length, "Security groups": byType("security_group").length,
  };
  const recent = (state?.cloudtrail || []).slice(0, 6);
  return (
    <div className="p-6 space-y-6" data-testid="aws-console-home">
      <div>
        <h1 className="text-2xl font-semibold text-white">Console Home</h1>
        <p className="text-slate-400 text-sm mt-1">HMG Global Enterprises · Account {state?.account_id} · {region}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-[#414750] bg-[#161e2d] p-4" data-testid={`home-count-${k}`}>
            <div className="text-3xl font-semibold text-white">{v}</div>
            <div className="text-xs text-slate-400 mt-1">{k} in {region}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-[#414750] bg-[#161e2d] p-4 lg:col-span-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><ScrollText className="h-4 w-4 text-[#ff9900]" /> Recent activity (CloudTrail)</div>
          <div className="mt-3 space-y-2">
            {recent.map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-xs text-slate-300">
                <span className="font-mono text-[#ff9900]">{e.event_name}</span>
                <span className="text-slate-500">{e.resource_name}</span>
                <span className="ml-auto text-slate-500">{new Date(e.event_time).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-[#414750] bg-[#161e2d] p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><DollarSign className="h-4 w-4 text-[#ff9900]" /> Estimated cost</div>
          <div className="text-3xl font-semibold text-white mt-2">${state?.cost?.monthly_estimate?.toFixed(2)}</div>
          <div className="text-xs text-slate-400">projected this month</div>
          <Button size="sm" variant="outline" className="mt-3 w-full border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => setView("cost")}>View breakdown</Button>
        </div>
      </div>
      <div className="rounded-lg border border-[#ff9900]/40 bg-[#ff9900]/5 p-4 flex items-center gap-3">
        <GraduationCap className="h-6 w-6 text-[#ff9900]" />
        <div className="text-sm text-slate-200">Learn by doing — open <strong>Guided AWS Labs</strong> (top-right) to work real production tickets and validate your configuration.</div>
      </div>
    </div>
  );
}

const EC2_SUBNAV = [["ec2", "Instances"], ["volumes", "Volumes"], ["snapshots", "Snapshots"], ["amis", "AMIs"], ["eips", "Elastic IPs"], ["keypairs", "Key Pairs"], ["enis", "Network Interfaces"]];
function Ec2SubNav({ view, setView }) {
  return <div className="flex gap-1 mb-4 flex-wrap" data-testid="ec2-subnav">
    {EC2_SUBNAV.map(([id, label]) => <button key={id} onClick={() => setView(id)} data-testid={`ec2-subnav-${id}`}
      className={`text-xs px-2.5 py-1 rounded ${view === id ? "bg-[#ff9900]/15 text-[#ff9900]" : "text-slate-400 hover:bg-white/5"}`}>{label}</button>)}</div>;
}

// ---------------- EC2 ----------------
function Ec2View({ setView, onOpenInstance, onLaunch }) {
  const { state, byType, action, run } = useAws();
  const instances = byType("instance");
  const [term, setTerm] = useState(null);

  const doAction = (id, act) => run(() => action("instance-action", { instance_id: id, action: act }), { success: `Instance ${act} ok` });

  return (
    <div className="p-6" data-testid="aws-ec2-view">
      <Ec2SubNav view="ec2" setView={setView} />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-white">EC2 · Instances</h1>
          <p className="text-slate-400 text-xs mt-0.5">{instances.length} instances in {state?.current_region}</p>
        </div>
        <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5" onClick={onLaunch} data-testid="ec2-launch-btn"><Plus className="h-4 w-4" /> Launch instances</Button>
      </div>
      <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]">
        <table className="w-full text-xs">
          <thead className="bg-[#1f2937] border-b border-[#414750]"><tr>
            <Th>Name</Th><Th>Instance ID</Th><Th>State</Th><Th>Type</Th><Th>OS</Th><Th>Public IP</Th><Th>Private IP</Th><Th>AZ</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {instances.map((i) => (
              <tr key={i.id} className="border-b border-[#2a323d] hover:bg-white/5" data-testid={`ec2-row-${i.name || i.id}`}>
                <Td><button className="text-[#4da3ff] hover:underline font-medium" onClick={() => onOpenInstance(i.id)} data-testid={`ec2-open-${i.name}`}>{i.name || "—"}</button></Td>
                <Td><button className="font-mono text-[#4da3ff] hover:underline" onClick={() => onOpenInstance(i.id)}>{i.id}</button></Td>
                <Td><StatusDot state={i.state} /></Td>
                <Td className="text-slate-300">{i.instance_type}</Td>
                <Td className="text-slate-300">{i.os}</Td>
                <Td className="text-slate-300">{i.public_ip || "—"}</Td>
                <Td className="text-slate-300">{i.private_ip || "—"}</Td>
                <Td className="text-slate-400">{i.az}</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {i.state === "stopped" && <button title="Start" onClick={() => doAction(i.id, "start")} className="p-1 hover:text-emerald-400" data-testid={`ec2-start-${i.name}`}><Play className="h-3.5 w-3.5" /></button>}
                    {i.state === "running" && <button title="Stop" onClick={() => doAction(i.id, "stop")} className="p-1 hover:text-amber-400" data-testid={`ec2-stop-${i.name}`}><Square className="h-3.5 w-3.5" /></button>}
                    {i.state === "running" && <button title="Reboot" onClick={() => doAction(i.id, "reboot")} className="p-1 hover:text-sky-400"><RotateCw className="h-3.5 w-3.5" /></button>}
                    {i.os === "Linux" && i.state === "running" && <button title="Connect (SSH)" onClick={() => setTerm(i)} className="p-1 hover:text-[#ff9900]" data-testid={`ec2-connect-${i.name}`}><TerminalSquare className="h-3.5 w-3.5" /></button>}
                    <button title="Details" onClick={() => onOpenInstance(i.id)} className="p-1 hover:text-white"><CircleDot className="h-3.5 w-3.5" /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {instances.length === 0 && <div className="p-4"><Empty label="No instances in this region. Launch one to get started." /></div>}
      </div>
      {term && <Ec2Terminal instance={term} onClose={() => setTerm(null)} />}
    </div>
  );
}

function Ec2Terminal({ instance, onClose }) {
  const [lines, setLines] = useState([`Connecting to ${instance.name} (${instance.public_ip || instance.private_ip}) via Session Manager…`]);
  const [cmd, setCmd] = useState("");
  const [cwd, setCwd] = useState(instance.terminal_cwd || "/home/ec2-user");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [lines]);

  const send = async () => {
    if (!cmd.trim() || busy) return;
    const c = cmd.trim();
    setLines((p) => [...p, `[ec2-user@${instance.name} ${cwd}]$ ${c}`]);
    setCmd(""); setBusy(true);
    try {
      const { data } = await api.post(`/aws/ec2/${instance.name}/exec`, { command: c, cwd });
      if (data.output) setLines((p) => [...p, data.output]);
      setCwd(data.cwd || cwd);
    } catch (e) {
      setLines((p) => [...p, e?.response?.data?.detail?.message || "Connection error."]);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0c0c0c] border-[#414750] text-slate-100 max-w-3xl" data-testid="ec2-terminal">
        <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><TerminalSquare className="h-4 w-4 text-[#ff9900]" /> {instance.name} — bash</DialogTitle></DialogHeader>
        <div className="font-mono text-xs bg-black rounded-md p-3 h-80 overflow-y-auto whitespace-pre-wrap">
          {lines.map((l, i) => <div key={i} className="text-emerald-300">{l}</div>)}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2">
          <input autoFocus value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a command (e.g. uname -a)" data-testid="ec2-term-input"
            className="flex-1 font-mono text-xs px-3 py-2 rounded bg-black border border-[#414750] text-emerald-300" />
          <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black" onClick={send} disabled={busy}>Run</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- VPC ----------------
function VpcView() {
  const { byType, action, run } = useAws();
  const vpcs = byType("vpc"), subnets = byType("subnet"), igws = byType("igw"),
    nats = byType("nat_gateway"), rts = byType("route_table");
  const [dlg, setDlg] = useState(null); // 'vpc'|'subnet'|'igw'|'rt'|'route'|'assoc'
  const q = (t) => setDlg(t);

  return (
    <div className="p-6 space-y-6" data-testid="aws-vpc-view">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">VPC · Networking</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => q("vpc")} data-testid="vpc-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> VPC</Button>
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => q("subnet")} data-testid="subnet-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Subnet</Button>
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => q("igw")} data-testid="igw-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Internet Gateway</Button>
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => q("rt")} data-testid="rt-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Route table</Button>
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => q("route")} data-testid="route-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Route</Button>
          <Button size="sm" variant="outline" className="border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => q("assoc")} data-testid="assoc-create-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Associate subnet</Button>
        </div>
      </div>

      <Section title="VPCs">
        <Table cols={["Name", "VPC ID", "CIDR", "State", "DNS hostnames"]}>
          {vpcs.map((v) => <tr key={v.id} className="border-b border-[#2a323d]" data-testid={`vpc-row-${v.name}`}>
            <Td className="text-white">{v.name || "—"}</Td><Td className="font-mono text-slate-400">{v.id}</Td>
            <Td className="text-slate-300">{v.cidr}</Td><Td><StatusDot state={v.state} /></Td><Td className="text-slate-400">{v.dns_hostnames ? "Enabled" : "Disabled"}</Td></tr>)}
        </Table>
      </Section>

      <Section title="Subnets">
        <Table cols={["Name", "Subnet ID", "VPC", "CIDR", "AZ", "Type"]}>
          {subnets.map((s) => {
            const rt = rts.find((x) => x.subnet_ids?.includes(s.id)) || rts.find((x) => x.vpc_id === s.vpc_id && x.main);
            const pub = !!rt && rt.routes.some((r) => r.target.startsWith("igw-"));
            return <tr key={s.id} className="border-b border-[#2a323d]" data-testid={`subnet-row-${s.cidr}`}>
              <Td className="text-white">{s.name || "—"}</Td><Td className="font-mono text-slate-400">{s.id}</Td>
              <Td className="font-mono text-slate-500">{s.vpc_id}</Td><Td className="text-slate-300">{s.cidr}</Td><Td className="text-slate-400">{s.az}</Td>
              <Td>{pub ? <span className="text-emerald-400 flex items-center gap-1"><Globe className="h-3 w-3" />Public</span> : <span className="text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" />Private</span>}
                <div className="text-[10px] text-slate-500 font-mono" data-testid={`subnet-rt-${s.cidr}`}>{rt ? `${rt.name || rt.id}${rt.main && !rt.subnet_ids?.includes(s.id) ? " (main, implicit)" : ""}` : "no route table"}</div></Td></tr>; })}
        </Table>
      </Section>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Internet Gateways">
          <Table cols={["Name", "ID", "State", "Attached VPC"]}>
            {igws.map((g) => <tr key={g.id} className="border-b border-[#2a323d]"><Td className="text-white">{g.name || "—"}</Td><Td className="font-mono text-slate-400">{g.id}</Td><Td><StatusDot state={g.state} /></Td><Td className="font-mono text-slate-500">{g.attached_vpc || "—"}</Td></tr>)}
          </Table>
        </Section>
        <Section title="NAT Gateways">
          <Table cols={["Name", "ID", "State", "Public IP"]}>
            {nats.map((n) => <tr key={n.id} className="border-b border-[#2a323d]"><Td className="text-white">{n.name || "—"}</Td><Td className="font-mono text-slate-400">{n.id}</Td><Td><StatusDot state={n.state} /></Td><Td className="text-slate-300">{n.public_ip}</Td></tr>)}
          </Table>
        </Section>
      </div>

      <Section title="Route tables">
        <Table cols={["Name", "ID", "VPC", "Routes", "Subnets"]}>
          {rts.map((rt) => <tr key={rt.id} className="border-b border-[#2a323d]"><Td className="text-white">{rt.name || "—"}{rt.main && <span className="ml-1 text-[10px] text-slate-500">(main)</span>}</Td>
            <Td className="font-mono text-slate-400">{rt.id}</Td><Td className="font-mono text-slate-500">{rt.vpc_id}</Td>
            <Td className="text-slate-300 text-[11px]">{rt.routes.map((r, i) => <div key={i}>{r.destination} → {r.target}</div>)}</Td>
            <Td className="text-slate-400 text-[11px]">{rt.subnet_ids?.length ? rt.subnet_ids.map((id) => subnets.find((s) => s.id === id)?.cidr || id).join(", ") : (rt.main ? "all unassociated" : "0")}</Td></tr>)}
        </Table>
      </Section>

      {dlg && <VpcDialogs type={dlg} onClose={() => setDlg(null)} />}
    </div>
  );
}

function VpcDialogs({ type, onClose }) {
  const { byType, action, run, state } = useAws();
  const vpcs = byType("vpc"), subnets = byType("subnet"), igws = byType("igw"), rts = byType("route_table");
  const azs = state?.catalog?.regions?.find((r) => r.code === state.current_region)?.azs || [];
  const [f, setF] = useState({ cidr: "", name: "", vpc_id: vpcs[0]?.id, az: azs[0], public: false,
    igw_id: igws[0]?.id, rtb_id: rts[0]?.id, destination: "0.0.0.0/0", target: igws[0]?.id, subnet_id: subnets[0]?.id });
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const go = async () => {
    setBusy(true);
    let call;
    if (type === "vpc") call = () => action("create-vpc", { cidr: f.cidr, name: f.name });
    if (type === "subnet") call = () => action("create-subnet", { vpc_id: f.vpc_id, cidr: f.cidr, az: f.az, name: f.name, public: f.public });
    if (type === "igw") call = async () => { const r = await action("create-igw", { name: f.name }); if (f.vpc_id) await action("attach-igw", { igw_id: r.data.result.id, vpc_id: f.vpc_id }); return r; };
    if (type === "route") call = () => action("create-route", { rtb_id: f.rtb_id, destination: f.destination, target: f.target });
    if (type === "rt") call = () => action("create-route-table", { vpc_id: f.vpc_id, name: f.name });
    if (type === "assoc") call = () => action("associate-route-table", { rtb_id: f.rtb_id, subnet_id: f.subnet_id });
    const r = await run(call, { success: type === "assoc" ? "Subnet associated" : "Created" });
    setBusy(false);
    if (r.ok) onClose();
  };

  const titles = { vpc: "Create VPC", subnet: "Create subnet", igw: "Create & attach Internet Gateway", route: "Add route", rt: "Create route table", assoc: "Associate route table with subnet" };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
        <DialogHeader><DialogTitle className="text-white">{titles[type]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {type === "vpc" && <>
            <Field label="Name"><Input value={f.name} onChange={(e) => up("name", e.target.value)} className="bg-[#0f1b2d] border-[#414750]" data-testid="vpc-name-input" /></Field>
            <Field label="IPv4 CIDR block"><Input value={f.cidr} onChange={(e) => up("cidr", e.target.value)} placeholder="10.30.0.0/16" className="bg-[#0f1b2d] border-[#414750]" data-testid="vpc-cidr-input" /></Field>
          </>}
          {type === "subnet" && <>
            <Field label="VPC"><Sel value={f.vpc_id} onChange={(v) => up("vpc_id", v)} items={vpcs.map((v) => [v.id, `${v.name} (${v.cidr})`])} testid="subnet-vpc-select" /></Field>
            <Field label="Name"><Input value={f.name} onChange={(e) => up("name", e.target.value)} className="bg-[#0f1b2d] border-[#414750]" /></Field>
            <Field label="CIDR block"><Input value={f.cidr} onChange={(e) => up("cidr", e.target.value)} placeholder="10.30.1.0/24" className="bg-[#0f1b2d] border-[#414750]" data-testid="subnet-cidr-input" /></Field>
            <Field label="Availability Zone"><Sel value={f.az} onChange={(v) => up("az", v)} items={azs.map((a) => [a, a])} /></Field>
          </>}
          {type === "igw" && <>
            <Field label="Name"><Input value={f.name} onChange={(e) => up("name", e.target.value)} className="bg-[#0f1b2d] border-[#414750]" data-testid="igw-name-input" /></Field>
            <Field label="Attach to VPC"><Sel value={f.vpc_id} onChange={(v) => up("vpc_id", v)} items={vpcs.map((v) => [v.id, `${v.name} (${v.cidr})`])} testid="igw-vpc-select" /></Field>
          </>}
          {type === "route" && <>
            <Field label="Route table"><Sel value={f.rtb_id} onChange={(v) => up("rtb_id", v)} items={rts.map((r) => [r.id, `${r.name || r.id}`])} testid="route-rt-select" /></Field>
            <Field label="Destination"><Input value={f.destination} onChange={(e) => up("destination", e.target.value)} className="bg-[#0f1b2d] border-[#414750]" data-testid="route-dest-input" /></Field>
            <Field label="Target (gateway)"><Sel value={f.target} onChange={(v) => up("target", v)} items={[...igws.map((g) => [g.id, `${g.name} (IGW)`]), ...byType("nat_gateway").map((n) => [n.id, `${n.name} (NAT)`])]} testid="route-target-select" /></Field>
            {rts.find((r) => r.id === f.rtb_id)?.main && f.target?.startsWith("igw-") && <div className="text-[11px] text-amber-400" data-testid="route-main-warning">Heads up: this is the VPC's main route table. Every subnet without its own association will become public. Prefer a dedicated public route table.</div>}
          </>}
          {type === "rt" && <>
            <Field label="VPC"><Sel value={f.vpc_id} onChange={(v) => up("vpc_id", v)} items={vpcs.map((v) => [v.id, `${v.name} (${v.cidr})`])} testid="rt-vpc-select" /></Field>
            <Field label="Name"><Input value={f.name} onChange={(e) => up("name", e.target.value)} placeholder="public-rt" className="bg-[#0f1b2d] border-[#414750]" data-testid="rt-name-input" /></Field>
            <div className="text-[11px] text-slate-400">A new route table only has the local VPC route, so any subnet you associate with it is private until you add an IGW route.</div>
          </>}
          {type === "assoc" && <>
            <Field label="Route table"><Sel value={f.rtb_id} onChange={(v) => up("rtb_id", v)} items={rts.map((r) => [r.id, `${r.name || r.id}${r.main ? " (main)" : ""}`])} testid="assoc-rt-select" /></Field>
            <Field label="Subnet"><Sel value={f.subnet_id} onChange={(v) => up("subnet_id", v)} items={subnets.map((s) => [s.id, `${s.cidr}${s.name ? ` · ${s.name}` : ""}`])} testid="assoc-subnet-select" /></Field>
          </>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Cancel</Button>
          <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={go} disabled={busy} data-testid="vpc-dialog-submit">{busy ? "Working…" : type === "assoc" ? "Associate" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Security Groups ----------------
function SgView() {
  const { byType, action, run, state } = useAws();
  const sgs = byType("security_group"), vpcs = byType("vpc");
  const [create, setCreate] = useState(false);
  const [ruleFor, setRuleFor] = useState(null);
  const [cf, setCf] = useState({ name: "", vpc_id: vpcs[0]?.id, description: "" });
  const [rf, setRf] = useState({ protocol: "tcp", from_port: 443, to_port: 443, cidr: "0.0.0.0/0" });
  const [busy, setBusy] = useState(false);

  const createSg = async () => { setBusy(true); const r = await run(() => action("create-security-group", cf), { success: "Security group created" }); setBusy(false); if (r.ok) setCreate(false); };
  const addRule = async () => { setBusy(true); const r = await run(() => action("authorize-sg-ingress", { sg_id: ruleFor.id, rule: { ...rf, from_port: Number(rf.from_port), to_port: Number(rf.to_port) } }), { success: "Inbound rule added" }); setBusy(false); if (r.ok) setRuleFor(null); };
  const delRule = (sg, idx) => run(() => action("revoke-sg-ingress", { sg_id: sg.id, index: idx }), { success: "Rule removed" });

  return (
    <div className="p-6 space-y-4" data-testid="aws-sg-view">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">EC2 · Security Groups</h1>
        <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5" onClick={() => { setCf({ name: "", vpc_id: vpcs[0]?.id, description: "" }); setCreate(true); }} data-testid="sg-create-btn"><Plus className="h-4 w-4" /> Create security group</Button>
      </div>
      {sgs.map((sg) => (
        <div key={sg.id} className="rounded-lg border border-[#414750] bg-[#161e2d]" data-testid={`sg-card-${sg.name}`}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#414750]">
            <Shield className="h-4 w-4 text-[#ff9900]" />
            <span className="font-medium text-white">{sg.name}</span>
            <span className="font-mono text-xs text-slate-500">{sg.id}</span>
            <span className="text-xs text-slate-500">{sg.description}</span>
            <Button size="sm" variant="outline" className="ml-auto border-[#414750] text-slate-200 hover:bg-white/5" onClick={() => { setRf({ protocol: "tcp", from_port: 443, to_port: 443, cidr: "0.0.0.0/0" }); setRuleFor(sg); }} data-testid={`sg-addrule-${sg.name}`}><Plus className="h-3.5 w-3.5 mr-1" /> Inbound rule</Button>
          </div>
          <div className="px-4 py-2">
            <div className="text-[11px] text-slate-400 mb-1">Inbound rules</div>
            {sg.ingress.length === 0 ? <div className="text-xs text-slate-500 py-2">No inbound rules — all inbound traffic is denied.</div> :
              <table className="w-full text-xs"><tbody>
                {sg.ingress.map((r, i) => <tr key={i} className="border-b border-[#2a323d] last:border-0" data-testid={`sg-rule-${sg.name}-${i}`}>
                  <Td className="text-slate-300">{r.protocol === "-1" ? "All traffic" : r.protocol.toUpperCase()}</Td>
                  <Td className="text-slate-300">{r.protocol === "-1" ? "All" : (r.from_port === r.to_port ? r.from_port : `${r.from_port}-${r.to_port}`)}</Td>
                  <Td className="text-slate-300">{r.cidr}</Td>
                  <Td className="text-right"><button onClick={() => delRule(sg, i)} className="text-slate-500 hover:text-rose-400" data-testid={`sg-delrule-${sg.name}-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></Td>
                </tr>)}
              </tbody></table>}
          </div>
        </div>
      ))}
      {sgs.length === 0 && <Empty label="No security groups in this region." />}

      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
          <DialogHeader><DialogTitle className="text-white">Create security group</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Name"><Input value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} placeholder="web-lab-sg" className="bg-[#0f1b2d] border-[#414750]" data-testid="sg-name-input" /></Field>
            <Field label="Description"><Input value={cf.description} onChange={(e) => setCf({ ...cf, description: e.target.value })} className="bg-[#0f1b2d] border-[#414750]" /></Field>
            <Field label="VPC"><Sel value={cf.vpc_id} onChange={(v) => setCf({ ...cf, vpc_id: v })} items={vpcs.map((v) => [v.id, `${v.name} (${v.cidr})`])} testid="sg-vpc-select" /></Field>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setCreate(false)} className="text-slate-300">Cancel</Button>
            <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={createSg} disabled={busy || !cf.name || !cf.vpc_id} data-testid="sg-create-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ruleFor} onOpenChange={(o) => !o && setRuleFor(null)}>
        <DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
          <DialogHeader><DialogTitle className="text-white">Add inbound rule — {ruleFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Type / Protocol"><Sel value={rf.protocol} onChange={(v) => setRf({ ...rf, protocol: v })} items={[["tcp", "TCP"], ["udp", "UDP"], ["-1", "All traffic"]]} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Port"><Input value={rf.from_port} onChange={(e) => setRf({ ...rf, from_port: e.target.value, to_port: e.target.value })} className="bg-[#0f1b2d] border-[#414750]" data-testid="sg-rule-port" /></Field>
              <Field label="Source (CIDR)"><Input value={rf.cidr} onChange={(e) => setRf({ ...rf, cidr: e.target.value })} className="bg-[#0f1b2d] border-[#414750]" data-testid="sg-rule-cidr" /></Field>
            </div>
            <div className="flex gap-2 text-[11px]">
              {[["SSH", 22], ["HTTP", 80], ["HTTPS", 443], ["PostgreSQL", 5432]].map(([l, p]) => <button key={p} className="px-2 py-1 rounded border border-[#414750] text-slate-300 hover:border-[#ff9900]" onClick={() => setRf({ ...rf, protocol: "tcp", from_port: p, to_port: p })}>{l}</button>)}
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setRuleFor(null)} className="text-slate-300">Cancel</Button>
            <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={addRule} disabled={busy} data-testid="sg-rule-submit">Add rule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- S3 ----------------
function S3View() {
  const { byType, action, run } = useAws();
  const buckets = byType("bucket");
  const [create, setCreate] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const createBucket = async () => { setBusy(true); const r = await run(() => action("create-bucket", { name }), { success: "Bucket created" }); setBusy(false); if (r.ok) { setCreate(false); setName(""); } };
  const toggleBpa = (b) => run(() => action("s3-block-public", { name: b.name, blocked: !b.block_public_access }), { success: "Block Public Access updated" });
  const toggleVer = (b) => run(() => action("s3-versioning", { name: b.name, enabled: !b.versioning }), { success: "Versioning updated" });

  return (
    <div className="p-6 space-y-4" data-testid="aws-s3-view">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">S3 · Buckets</h1>
        <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5" onClick={() => setCreate(true)} data-testid="s3-create-btn"><Plus className="h-4 w-4" /> Create bucket</Button>
      </div>
      <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]">
        <table className="w-full text-xs">
          <thead className="bg-[#1f2937] border-b border-[#414750]"><tr><Th>Name</Th><Th>Region</Th><Th>Objects</Th><Th>Access</Th><Th>Versioning</Th><Th>Encryption</Th></tr></thead>
          <tbody>
            {buckets.map((b) => {
              const isPublic = !b.block_public_access && (b.policy?.Statement || []).some((s) => s.Effect === "Allow" && (s.Principal === "*" || s.Principal?.AWS === "*"));
              return <tr key={b.name} className="border-b border-[#2a323d]" data-testid={`s3-row-${b.name}`}>
                <Td className="text-white font-medium flex items-center gap-2"><Boxes className="h-3.5 w-3.5 text-[#ff9900]" />{b.name}</Td>
                <Td className="text-slate-400">{b.region}</Td>
                <Td className="text-slate-300">{b.objects?.length || 0}</Td>
                <Td>{isPublic ? <span className="text-rose-400 font-medium">Public</span> : <span className="text-emerald-400">Not public</span>}
                  <button onClick={() => toggleBpa(b)} className="ml-2 text-[10px] underline text-slate-400 hover:text-white" data-testid={`s3-bpa-${b.name}`}>{b.block_public_access ? "unblock" : "Block Public Access"}</button></Td>
                <Td>{b.versioning ? <span className="text-emerald-400">Enabled</span> : <span className="text-slate-400">Disabled</span>}
                  <button onClick={() => toggleVer(b)} className="ml-2 text-[10px] underline text-slate-400 hover:text-white" data-testid={`s3-ver-${b.name}`}>{b.versioning ? "disable" : "enable"}</button></Td>
                <Td className="text-slate-400">{b.encryption}</Td>
              </tr>;
            })}
          </tbody>
        </table>
        {buckets.length === 0 && <div className="p-4"><Empty label="No buckets yet." /></div>}
      </div>
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
          <DialogHeader><DialogTitle className="text-white">Create bucket</DialogTitle></DialogHeader>
          <Field label="Bucket name (globally unique, lowercase)"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-lab-bucket-123" className="bg-[#0f1b2d] border-[#414750]" data-testid="s3-name-input" /></Field>
          <DialogFooter><Button variant="ghost" onClick={() => setCreate(false)} className="text-slate-300">Cancel</Button>
            <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={createBucket} disabled={busy || !name} data-testid="s3-create-submit">Create bucket</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- EC2 family: Volumes / Snapshots / AMIs / EIPs / KeyPairs / ENIs ----------------
function VolumesView({ setView }) {
  const { state, byType, action, run } = useAws();
  const vols = byType("volume").filter((v) => v.state !== "deleted");
  const instances = byType("instance");
  const [create, setCreate] = useState(false);
  const azs = state?.catalog?.regions?.find((r) => r.code === state.current_region)?.azs || [];
  const [f, setF] = useState({ az: azs[0], size_gb: 20, vol_type: "gp3", encrypted: false, name: "" });
  const [busy, setBusy] = useState(false);
  const [modify, setModify] = useState(null);
  const doCreate = async () => { setBusy(true); const r = await run(() => action("create-volume", f), { success: "Volume created" }); setBusy(false); if (r.ok) setCreate(false); };
  const detach = (v) => run(() => action("detach-volume", { vol_id: v.id }), { success: "Detached" });
  const del = (v) => run(() => action("delete-volume", { vol_id: v.id }), { success: "Deleted" });
  const snap = (v) => run(() => action("create-snapshot", { vol_id: v.id }), { success: "Snapshot created" });
  const attach = (v, iid) => run(() => action("attach-volume", { vol_id: v.id, instance_id: iid }), { success: "Attached" });

  return <div className="p-6" data-testid="aws-volumes-view"><Ec2SubNav view="volumes" setView={setView} />
    <div className="flex items-center justify-between mb-4"><h1 className="text-xl font-semibold text-white">EC2 · Volumes (EBS)</h1>
      <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5" onClick={() => setCreate(true)} data-testid="vol-create-btn"><Plus className="h-4 w-4" /> Create volume</Button></div>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><table className="w-full text-xs">
      <thead className="bg-[#1f2937] border-b border-[#414750]"><tr><Th>Volume ID</Th><Th>Size</Th><Th>Type</Th><Th>State</Th><Th>Attached to</Th><Th>AZ</Th><Th>Encrypted</Th><Th>Actions</Th></tr></thead>
      <tbody>{vols.map((v) => <tr key={v.id} className="border-b border-[#2a323d]" data-testid={`vol-row-${v.id}`}>
        <Td className="font-mono text-slate-300">{v.id}</Td><Td className="text-slate-300">{v.size_gb} GiB</Td><Td className="text-slate-300">{v.vol_type}</Td>
        <Td><StatusDot state={v.state} /></Td><Td className="text-slate-400">{instances.find((i) => i.id === v.instance_id)?.name || "—"}</Td>
        <Td className="text-slate-400">{v.az}</Td><Td className="text-slate-400">{v.encrypted ? "Yes" : "No"}</Td>
        <Td><div className="flex items-center gap-1">
          <button title="Snapshot" onClick={() => snap(v)} className="p-1 hover:text-[#ff9900]" data-testid={`vol-snap-${v.id}`}><HardDrive className="h-3.5 w-3.5" /></button>
          <button title="Modify size" onClick={() => setModify(v)} className="p-1 hover:text-sky-400" data-testid={`vol-modify-${v.id}`}><Plus className="h-3.5 w-3.5" /></button>
          {v.state === "in-use" ? <button title="Detach" onClick={() => detach(v)} className="p-1 hover:text-amber-400" data-testid={`vol-detach-${v.id}`}><Square className="h-3.5 w-3.5" /></button>
            : <>{instances.filter((i) => i.az === v.az).slice(0, 1).map((i) => <button key={i.id} title={`Attach to ${i.name}`} onClick={() => attach(v, i.id)} className="p-1 hover:text-emerald-400" data-testid={`vol-attach-${v.id}`}><Play className="h-3.5 w-3.5" /></button>)}
              <button title="Delete" onClick={() => del(v)} className="p-1 hover:text-rose-400" data-testid={`vol-del-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></button></>}
        </div></Td></tr>)}</tbody></table>
      {vols.length === 0 && <div className="p-4"><Empty label="No volumes." /></div>}</div>
    <Dialog open={create} onOpenChange={setCreate}><DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
      <DialogHeader><DialogTitle className="text-white">Create volume</DialogTitle></DialogHeader>
      <div className="space-y-3"><Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[#0f1b2d] border-[#414750]" /></Field>
        <div className="grid grid-cols-2 gap-3"><Field label="Size (GiB)"><Input type="number" value={f.size_gb} onChange={(e) => setF({ ...f, size_gb: e.target.value })} className="bg-[#0f1b2d] border-[#414750]" data-testid="vol-size" /></Field>
        <Field label="AZ"><Sel value={f.az} onChange={(v) => setF({ ...f, az: v })} items={azs.map((a) => [a, a])} /></Field></div>
        <Field label="Type"><Sel value={f.vol_type} onChange={(v) => setF({ ...f, vol_type: v })} items={[["gp3", "gp3"], ["gp2", "gp2"], ["io1", "io1"]]} /></Field></div>
      <DialogFooter><Button variant="ghost" onClick={() => setCreate(false)} className="text-slate-300">Cancel</Button><Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={doCreate} disabled={busy} data-testid="vol-create-submit">Create</Button></DialogFooter></DialogContent></Dialog>
    {modify && <ModifyVol vol={modify} onClose={() => setModify(null)} />}
  </div>;
}
function ModifyVol({ vol, onClose }) {
  const { action, run } = useAws(); const [size, setSize] = useState(vol.size_gb); const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); const r = await run(() => action("modify-volume", { vol_id: vol.id, size_gb: Number(size) }), { success: "Volume modified" }); setBusy(false); if (r.ok) onClose(); };
  return <Dialog open onOpenChange={onClose}><DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
    <DialogHeader><DialogTitle className="text-white">Modify volume {vol.id}</DialogTitle></DialogHeader>
    <Field label="New size (GiB, increase only)"><Input type="number" value={size} onChange={(e) => setSize(e.target.value)} className="bg-[#0f1b2d] border-[#414750]" data-testid="vol-modify-size" /></Field>
    <DialogFooter><Button variant="ghost" onClick={onClose} className="text-slate-300">Cancel</Button><Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={go} disabled={busy} data-testid="vol-modify-submit">Apply</Button></DialogFooter></DialogContent></Dialog>;
}
function SnapshotsView({ setView }) {
  const { byType } = useAws(); const snaps = byType("snapshot");
  return <div className="p-6" data-testid="aws-snapshots-view"><Ec2SubNav view="snapshots" setView={setView} /><h1 className="text-xl font-semibold text-white mb-4">EC2 · Snapshots</h1>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["Snapshot ID", "Name", "Source volume", "Size", "State", "Progress"]}>
      {snaps.map((s) => <tr key={s.id} className="border-b border-[#2a323d]" data-testid={`snap-row-${s.id}`}><Td className="font-mono text-slate-300">{s.id}</Td><Td className="text-white">{s.name}</Td><Td className="font-mono text-slate-400">{s.volume_id || "—"}</Td><Td className="text-slate-300">{s.size_gb} GiB</Td><Td className="text-emerald-400">{s.state}</Td><Td className="text-slate-400">{s.progress}</Td></tr>)}
    </Table>{snaps.length === 0 && <div className="p-4"><Empty label="No snapshots. Create one from a volume or an AMI." /></div>}</div></div>;
}
function AmisView({ setView, onLaunch }) {
  const { byType } = useAws(); const amis = byType("ami");
  return <div className="p-6" data-testid="aws-amis-view"><Ec2SubNav view="amis" setView={setView} /><h1 className="text-xl font-semibold text-white mb-4">EC2 · AMIs (My images)</h1>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["AMI ID", "Name", "OS", "Source", "Visibility", "State"]}>
      {amis.map((a) => <tr key={a.id} className="border-b border-[#2a323d]" data-testid={`ami-row-${a.id}`}><Td className="font-mono text-slate-300">{a.id}</Td><Td className="text-white">{a.name}</Td><Td className="text-slate-300">{a.os}</Td><Td className="font-mono text-slate-400">{a.source_instance || "—"}</Td><Td className="text-slate-400">{a.visibility}</Td><Td className="text-emerald-400">{a.state}</Td></tr>)}
    </Table>{amis.length === 0 && <div className="p-4"><Empty label="No custom AMIs yet. Use an instance's Actions → Create image." /></div>}</div></div>;
}
function EipsView({ setView }) {
  const { byType, action, run } = useAws(); const eips = byType("eip"); const instances = byType("instance");
  const allocate = () => run(() => action("allocate-eip", {}), { success: "Elastic IP allocated" });
  const disassoc = (e) => run(() => action("disassociate-eip", { eip_id: e.id }), { success: "Disassociated" });
  const release = (e) => run(() => action("release-eip", { eip_id: e.id }), { success: "Released" });
  return <div className="p-6" data-testid="aws-eips-view"><Ec2SubNav view="eips" setView={setView} />
    <div className="flex items-center justify-between mb-4"><h1 className="text-xl font-semibold text-white">EC2 · Elastic IPs</h1>
      <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5" onClick={allocate} data-testid="eip-allocate-btn"><Plus className="h-4 w-4" /> Allocate Elastic IP</Button></div>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["Allocation ID", "Public IP", "Associated instance", "Actions"]}>
      {eips.map((e) => <tr key={e.id} className="border-b border-[#2a323d]" data-testid={`eip-row-${e.public_ip}`}><Td className="font-mono text-slate-300">{e.id}</Td><Td className="text-white">{e.public_ip}</Td><Td className="text-slate-400">{instances.find((i) => i.id === e.instance_id)?.name || "—"}</Td>
        <Td><div className="flex gap-1">{e.instance_id ? <button onClick={() => disassoc(e)} className="p-1 hover:text-amber-400" data-testid={`eip-disassoc-${e.public_ip}`} title="Disassociate"><Square className="h-3.5 w-3.5" /></button>
          : <button onClick={() => release(e)} className="p-1 hover:text-rose-400" data-testid={`eip-release-${e.public_ip}`} title="Release"><Trash2 className="h-3.5 w-3.5" /></button>}</div></Td></tr>)}
    </Table>{eips.length === 0 && <div className="p-4"><Empty label="No Elastic IPs allocated." /></div>}</div></div>;
}
function KeyPairsView({ setView }) {
  const { byType, action, run } = useAws(); const keys = byType("key_pair");
  const [name, setName] = useState(""); const [create, setCreate] = useState(false); const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); const r = await run(() => action("create-key-pair-only", { name }), { success: "Key pair created" }); setBusy(false); if (r.ok) { setCreate(false); setName(""); } };
  return <div className="p-6" data-testid="aws-keypairs-view"><Ec2SubNav view="keypairs" setView={setView} />
    <div className="flex items-center justify-between mb-4"><h1 className="text-xl font-semibold text-white">EC2 · Key Pairs</h1>
      <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5" onClick={() => setCreate(true)} data-testid="kp-create-btn"><Plus className="h-4 w-4" /> Create key pair</Button></div>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["Name", "ID", "Fingerprint"]}>
      {keys.map((k) => <tr key={k.id} className="border-b border-[#2a323d]" data-testid={`kp-row-${k.name}`}><Td className="text-white">{k.name}</Td><Td className="font-mono text-slate-400">{k.id}</Td><Td className="font-mono text-slate-500 text-[10px]">{k.fingerprint}</Td></tr>)}
    </Table>{keys.length === 0 && <div className="p-4"><Empty label="No key pairs." /></div>}</div>
    <Dialog open={create} onOpenChange={setCreate}><DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
      <DialogHeader><DialogTitle className="text-white">Create key pair</DialogTitle></DialogHeader>
      <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-key" className="bg-[#0f1b2d] border-[#414750]" data-testid="kp-name-input" /></Field>
      <DialogFooter><Button variant="ghost" onClick={() => setCreate(false)} className="text-slate-300">Cancel</Button><Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={go} disabled={busy || !name} data-testid="kp-create-submit">Create</Button></DialogFooter></DialogContent></Dialog></div>;
}
function EnisView({ setView }) {
  const { byType } = useAws(); const instances = byType("instance");
  const enis = instances.map((i) => ({ id: "eni-" + i.id.slice(2, 12), instance: i.name, subnet_id: i.subnet_id, private_ip: i.private_ip, public_ip: i.public_ip, sgs: i.security_group_ids?.length || 0, status: i.state === "running" ? "in-use" : "available" }));
  return <div className="p-6" data-testid="aws-enis-view"><Ec2SubNav view="enis" setView={setView} /><h1 className="text-xl font-semibold text-white mb-4">EC2 · Network Interfaces</h1>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["ENI ID", "Attached instance", "Subnet", "Private IP", "Public IP", "Security groups", "Status"]}>
      {enis.map((e) => <tr key={e.id} className="border-b border-[#2a323d]" data-testid={`eni-row-${e.id}`}><Td className="font-mono text-slate-300">{e.id}</Td><Td className="text-white">{e.instance}</Td><Td className="font-mono text-slate-400">{e.subnet_id}</Td><Td className="text-slate-300">{e.private_ip}</Td><Td className="text-slate-300">{e.public_ip || "—"}</Td><Td className="text-slate-400">{e.sgs}</Td><Td className="text-emerald-400">{e.status}</Td></tr>)}
    </Table></div></div>;
}

// ---------------- IAM ----------------
function IamView({ onOpenUser, onOpenRole }) {
  const { state, action, run } = useAws();
  const users = state?.iam?.users || [], roles = state?.iam?.roles || [], groups = state?.iam?.groups || [];
  const [create, setCreate] = useState(null); // 'user'|'role'
  const [name, setName] = useState(""); const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); const r = await run(() => action(create === "user" ? "iam-create-user" : "iam-create-role", { name }), { success: `${create} created` }); setBusy(false); if (r.ok) { setCreate(null); setName(""); } };
  return (
    <div className="p-6 space-y-6" data-testid="aws-iam-view">
      <h1 className="text-xl font-semibold text-white">IAM · Identity & Access Management</h1>
      <div>
        <div className="flex items-center justify-between mb-2"><div className="text-sm font-semibold text-white">Users ({users.length})</div>
          <Button size="sm" className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={() => { setName(""); setCreate("user"); }} data-testid="iam-create-user-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Create user</Button></div>
        <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["User", "Groups", "MFA", "Access key", "Created"]}>
          {users.map((u) => <tr key={u.name} className="border-b border-[#2a323d]" data-testid={`iam-user-row-${u.name}`}>
            <Td><button className="text-[#4da3ff] hover:underline font-medium" onClick={() => onOpenUser(u.name)} data-testid={`iam-open-user-${u.name}`}>{u.name}</button></Td>
            <Td className="text-slate-300">{(u.groups || []).join(", ") || "—"}</Td><Td>{u.mfa_enabled ? <span className="text-emerald-400">Enabled</span> : <span className="text-amber-400">Not enabled</span>}</Td>
            <Td className="font-mono text-slate-500">{u.access_keys?.[0]?.id || "—"}</Td><Td className="text-slate-400">{new Date(u.created_at).toLocaleDateString()}</Td></tr>)}
        </Table></div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><div className="text-sm font-semibold text-white">Roles ({roles.length})</div>
          <Button size="sm" className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={() => { setName(""); setCreate("role"); }} data-testid="iam-create-role-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Create role</Button></div>
        <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]"><Table cols={["Role", "Trusted entity", "Policies", "Used by"]}>
          {roles.map((r) => { const used = (state?.resources || []).filter((x) => x.type === "instance" && x.iam_role === r.name && x.state !== "terminated");
            return <tr key={r.name} className="border-b border-[#2a323d]" data-testid={`iam-role-row-${r.name}`}>
            <Td><button className="text-[#4da3ff] hover:underline font-medium" onClick={() => onOpenRole(r.name)} data-testid={`iam-open-role-${r.name}`}>{r.name}</button></Td>
            <Td className="text-slate-300">{r.trust}</Td><Td className="text-slate-400">{r.policies.join(", ") || "—"}</Td><Td className="text-slate-400">{used.length ? `${used.length} instance(s)` : "—"}</Td></tr>; })}
        </Table></div>
      </div>
      <Section title={`Groups (${groups.length})`}>
        <Table cols={["Group", "Attached policies"]}>
          {groups.map((g) => <tr key={g.name} className="border-b border-[#2a323d]"><Td className="text-white">{g.name}</Td><Td className="text-slate-400">{g.policies.join(", ")}</Td></tr>)}
        </Table>
      </Section>
      <Dialog open={!!create} onOpenChange={(o) => !o && setCreate(null)}><DialogContent className="bg-[#161e2d] border-[#414750] text-slate-100">
        <DialogHeader><DialogTitle className="text-white">Create {create}</DialogTitle></DialogHeader>
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0f1b2d] border-[#414750]" data-testid="iam-create-name" /></Field>
        <DialogFooter><Button variant="ghost" onClick={() => setCreate(null)} className="text-slate-300">Cancel</Button><Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={go} disabled={busy || !name} data-testid="iam-create-submit">Create</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}

// ---------------- CloudTrail ----------------
function CloudTrailView() {
  const { state } = useAws();
  const events = state?.cloudtrail || [];
  return (
    <div className="p-6" data-testid="aws-cloudtrail-view">
      <h1 className="text-xl font-semibold text-white mb-4">CloudTrail · Event history</h1>
      <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]">
        <table className="w-full text-xs"><thead className="bg-[#1f2937] border-b border-[#414750]"><tr>
          <Th>Event time</Th><Th>Event name</Th><Th>User</Th><Th>Event source</Th><Th>Resource</Th><Th>Region</Th><Th>Result</Th></tr></thead>
          <tbody>{events.map((e) => <tr key={e.id} className="border-b border-[#2a323d]" data-testid="cloudtrail-row">
            <Td className="text-slate-400">{new Date(e.event_time).toLocaleString()}</Td><Td className="text-white font-mono">{e.event_name}</Td>
            <Td className="text-slate-300">{e.username}</Td><Td className="text-slate-400">{e.event_source}</Td><Td className="text-slate-400">{e.resource_name || "—"}</Td>
            <Td className="text-slate-400">{e.aws_region}</Td><Td className="text-emerald-400">{e.result}</Td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- CloudWatch ----------------
function CloudWatchView() {
  const { state, byType } = useAws();
  const alarms = state?.alarms || [];
  const instances = byType("instance");
  return (
    <div className="p-6 space-y-6" data-testid="aws-cloudwatch-view">
      <h1 className="text-xl font-semibold text-white">CloudWatch · Monitoring</h1>
      <Section title="Alarms">
        <Table cols={["Alarm", "Metric", "Resource", "Threshold", "State"]}>
          {alarms.map((a) => <tr key={a.id} className="border-b border-[#2a323d]"><Td className="text-white">{a.name}</Td><Td className="text-slate-300">{a.metric}</Td><Td className="text-slate-400">{a.resource}</Td><Td className="text-slate-400">{a.threshold}%</Td><Td className="text-emerald-400">{a.state}</Td></tr>)}
        </Table>
      </Section>
      <Section title="EC2 CPU utilization (last 24h, simulated)">
        <div className="grid sm:grid-cols-2 gap-4 p-2">
          {instances.filter((i) => i.state === "running").slice(0, 4).map((i) => <MiniChart key={i.id} instance={i} />)}
        </div>
      </Section>
    </div>
  );
}

function MiniChart({ instance }) {
  const [pts, setPts] = useState([]);
  useEffect(() => {
    api.get(`/aws/cloudwatch/metrics?namespace=AWS/EC2&metric=CPUUtilization&dimension=${instance.name}`).then(({ data }) => setPts(data.datapoints)).catch(() => {});
  }, [instance.name]);
  const max = Math.max(...pts, 100);
  return (
    <div className="rounded-lg border border-[#414750] bg-[#0f1b2d] p-3">
      <div className="text-xs text-white flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5 text-[#ff9900]" /> {instance.name}</div>
      <div className="flex items-end gap-0.5 h-16 mt-2">
        {pts.map((p, i) => <div key={i} className="flex-1 bg-[#ff9900]/70 rounded-sm" style={{ height: `${(p / max) * 100}%` }} title={`${p}%`} />)}
      </div>
    </div>
  );
}

// ---------------- Cost ----------------
function CostView() {
  const { state } = useAws();
  const cost = state?.cost || { monthly_estimate: 0, by_service: [] };
  return (
    <div className="p-6 space-y-6" data-testid="aws-cost-view">
      <h1 className="text-xl font-semibold text-white">Billing & Cost Management</h1>
      <div className="rounded-lg border border-[#414750] bg-[#161e2d] p-6">
        <div className="text-slate-400 text-sm">Estimated total (this month)</div>
        <div className="text-4xl font-semibold text-white mt-1">${cost.monthly_estimate.toFixed(2)}</div>
      </div>
      <Section title="Cost by service">
        <Table cols={["Service", "Monthly estimate"]}>
          {cost.by_service.map((s) => <tr key={s.service} className="border-b border-[#2a323d]"><Td className="text-white">{s.service}</Td><Td className="text-[#ff9900] font-medium">${s.monthly.toFixed(2)}</Td></tr>)}
        </Table>
      </Section>
      <p className="text-xs text-slate-500">Estimates are illustrative training figures based on approximate on-demand rates. Not actual AWS billing.</p>
    </div>
  );
}

// ---------------- CloudShell ----------------
function CloudShellView() {
  const [lines, setLines] = useState(["Welcome to AWS CloudShell (HMG Training). The CLI operates on the SAME state as the console.", "Try: aws ec2 describe-instances | aws s3 ls | aws sts get-caller-identity"]);
  const [cmd, setCmd] = useState("");
  const { refresh } = useAws();
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [lines]);
  const send = async () => {
    if (!cmd.trim()) return;
    const c = cmd.trim();
    setLines((p) => [...p, `[cloudshell-user@ip-10-0-0-1 ~]$ ${c}`]);
    setCmd("");
    try { const { data } = await api.post("/aws/cloudshell", { command: c }); setLines((p) => [...p, data.output]); await refresh(); }
    catch { setLines((p) => [...p, "Error running command."]); }
  };
  return (
    <div className="p-6 h-full flex flex-col" data-testid="aws-cloudshell-view">
      <h1 className="text-xl font-semibold text-white mb-3 flex items-center gap-2"><TerminalSquare className="h-5 w-5 text-[#ff9900]" /> CloudShell</h1>
      <div className="flex-1 font-mono text-xs bg-black rounded-md p-3 overflow-y-auto whitespace-pre-wrap min-h-[300px]">
        {lines.map((l, i) => <div key={i} className="text-emerald-300">{l}</div>)}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-2">
        <input autoFocus value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="aws ..." data-testid="cloudshell-input"
          className="flex-1 font-mono text-xs px-3 py-2 rounded bg-black border border-[#414750] text-emerald-300" />
        <Button className="bg-[#ff9900] hover:bg-[#ec7211] text-black" onClick={send} data-testid="cloudshell-run">Run</Button>
      </div>
    </div>
  );
}

// ---------------- shared small components ----------------
function Section({ title, children }) {
  return <div><div className="text-sm font-semibold text-white mb-2">{title}</div>
    <div className="rounded-lg border border-[#414750] overflow-x-auto bg-[#161e2d]">{children}</div></div>;
}
function Table({ cols, children }) {
  return <table className="w-full text-xs"><thead className="bg-[#1f2937] border-b border-[#414750]"><tr>{cols.map((c) => <Th key={c}>{c}</Th>)}</tr></thead><tbody>{children}</tbody></table>;
}
function Field({ label, children }) { return <div><label className="text-xs text-slate-400">{label}</label>{children}</div>; }
function Sel({ value, onChange, items, testid }) {
  return <Select value={value} onValueChange={onChange}><SelectTrigger className="bg-[#0f1b2d] border-[#414750]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>;
}

// ---------------- shell ----------------
function ConsoleShell() {
  const { state, loading, identity, region, regions, setRegion, switchIdentity } = useAws();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState("home");
  const [guided, setGuided] = useState(false);
  const [initialScenario, setInitialScenario] = useState(null);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [acctOpen, setAcctOpen] = useState(false);
  const [detail, setDetail] = useState(null); // {type:'instance'|'iam-user'|'iam-role', id}
  const [wizard, setWizard] = useState(false);
  const [connectInst, setConnectInst] = useState(null);

  const openInstance = (id) => setDetail({ type: "instance", id });
  const openUser = (name) => setDetail({ type: "iam-user", id: name });
  const openRole = (name) => setDetail({ type: "iam-role", id: name });
  const goView = (v) => { setDetail(null); setView(v); };

  useEffect(() => {
    const g = searchParams.get("guided");
    if (g) { setInitialScenario(g); setGuided(true); }
  }, [searchParams]);

  const filteredServices = useMemo(() =>
    SERVICES.filter((s) => s.label.toLowerCase().includes(search.toLowerCase())), [search]);

  if (loading) return <div className="h-screen grid place-items-center bg-[#0f1b2d] text-slate-300">Loading AWS Lab…</div>;

  const Views = { home: <ConsoleHome setView={setView} />,
    topology: <AwsTopology goView={goView} />,
    ec2: <Ec2View setView={goView} onOpenInstance={openInstance} onLaunch={() => setWizard(true)} />,
    volumes: <VolumesView setView={goView} />, snapshots: <SnapshotsView setView={goView} />,
    amis: <AmisView setView={goView} />, eips: <EipsView setView={goView} />,
    keypairs: <KeyPairsView setView={goView} />, enis: <EnisView setView={goView} />,
    vpc: <VpcView />, "security-groups": <SgView />, s3: <S3View />,
    "load-balancers": <LoadBalancersView region={region} goView={goView} />,
    "auto-scaling": <AutoScalingView region={region} goView={goView} />,
    rds: <RdsView region={region} />, cache: <ElastiCacheView region={region} />,
    route53: <Route53View />, cloudfront: <CloudFrontView />, waf: <WafView />,
    kms: <KmsView region={region} />, secrets: <SecretsView region={region} />, config: <ConfigView />,
    iam: <IamView onOpenUser={openUser} onOpenRole={openRole} />, cloudtrail: <CloudTrailView />,
    cloudwatch: <CloudWatchView />, cost: <CostView />, cloudshell: <CloudShellView /> };

  let mainContent = Views[view];
  if (detail?.type === "instance") mainContent = <Ec2Detail instanceId={detail.id} goBack={() => setDetail(null)} openRole={openRole} goView={goView} onConnect={setConnectInst} />;
  if (detail?.type === "iam-user") mainContent = <IamUserDetail userName={detail.id} goBack={() => setDetail(null)} />;
  if (detail?.type === "iam-role") mainContent = <IamRoleDetail roleName={detail.id} goBack={() => setDetail(null)} listInstances={() => goView("ec2")} />;

  return (
    <div className="h-screen flex flex-col bg-[#0f1b2d] text-slate-100 overflow-hidden">
      {/* top bar */}
      <header className="h-[40px] shrink-0 bg-[#232f3e] flex items-center px-3 gap-3 text-sm border-b border-black/40 relative z-50">
        <button onClick={() => navigate("/labs")} title="Back to HMG Labs" className="text-slate-300 hover:text-white flex items-center gap-1" data-testid="aws-back-btn"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex items-center gap-1.5 font-semibold">
          <div className="h-6 w-6 rounded bg-[#ff9900] grid place-items-center text-black text-xs font-bold">aws</div>
          <span className="text-white hidden sm:inline">HMG Training Lab</span>
        </div>
        <div className="relative">
          <button onClick={() => setServicesOpen((o) => !o)} className="flex items-center gap-1 text-slate-200 hover:text-white px-2 py-1 rounded hover:bg-white/10" data-testid="aws-services-btn"><Boxes className="h-4 w-4" /> Services <ChevronDown className="h-3 w-3" /></button>
          {servicesOpen && (
            <div className="absolute top-9 left-0 w-72 bg-[#161e2d] border border-[#414750] rounded-md shadow-2xl p-2 max-h-[70vh] overflow-y-auto" data-testid="aws-services-menu">
              {SERVICES.filter((s) => s.cat).map((s) => (
                <button key={s.id} onClick={() => { goView(s.id); setServicesOpen(false); }} className="w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-white/10 text-left text-slate-200" data-testid={`aws-svc-${s.id}`}>
                  <s.icon className="h-4 w-4 text-[#ff9900]" /><span>{s.label}</span><span className="ml-auto text-[10px] text-slate-500">{s.cat}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative flex-1 max-w-md hidden md:block">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search for services, features…" data-testid="aws-search"
            className="w-full bg-[#0f1b2d] border border-[#414750] rounded pl-7 pr-2 py-1 text-xs text-slate-200" />
          {search && (
            <div className="absolute top-8 left-0 w-full bg-[#161e2d] border border-[#414750] rounded-md shadow-2xl p-1 z-50">
              {filteredServices.map((s) => <button key={s.id} onClick={() => { goView(s.id); setSearch(""); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/10 text-left text-xs text-slate-200"><s.icon className="h-3.5 w-3.5 text-[#ff9900]" />{s.label}</button>)}
              {filteredServices.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">No matches</div>}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded bg-[#ff9900]/20 text-[#ff9900] border border-[#ff9900]/40 font-medium hidden sm:inline" data-testid="aws-sim-badge">SIMULATION MODE</span>
          <Button size="sm" className="bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold gap-1.5 h-7" onClick={() => setGuided(true)} data-testid="aws-guided-open"><GraduationCap className="h-4 w-4" /> <span className="hidden sm:inline">Guided Labs</span></Button>
          {/* region selector */}
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="h-7 bg-transparent border-none text-slate-200 hover:bg-white/10 text-xs gap-1 w-auto" data-testid="aws-region-select"><Globe className="h-3.5 w-3.5" /><SelectValue /></SelectTrigger>
            <SelectContent>{regions.map((r) => <SelectItem key={r.code} value={r.code}>{r.name} ({r.code})</SelectItem>)}</SelectContent>
          </Select>
          {/* account / role menu */}
          <div className="relative">
            <button onClick={() => setAcctOpen((o) => !o)} className="flex items-center gap-1 text-slate-200 hover:text-white px-2 py-1 rounded hover:bg-white/10 text-xs" data-testid="aws-account-btn">
              <Users className="h-3.5 w-3.5" /> {identity?.name} <ChevronDown className="h-3 w-3" />
            </button>
            {acctOpen && (
              <div className="absolute top-9 right-0 w-64 bg-[#161e2d] border border-[#414750] rounded-md shadow-2xl p-2 z-50" data-testid="aws-account-menu">
                <div className="px-2 py-1 text-[11px] text-slate-400">Account: {state?.account_id}</div>
                <div className="px-2 py-1 text-[11px] text-slate-500 border-b border-[#414750] mb-1">Switch role (IAM):</div>
                {state?.iam?.identities?.map((i) => (
                  <button key={i.id} onClick={() => { switchIdentity(i.id); setAcctOpen(false); }} className={`w-full text-left px-2 py-2 rounded hover:bg-white/10 ${i.is_current ? "text-[#ff9900]" : "text-slate-200"}`} data-testid={`aws-role-${i.name}`}>
                    <div className="text-xs font-medium">{i.name}{i.is_current && " ✓"}</div>
                    <div className="text-[10px] text-slate-500">{i.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* left service nav */}
        <nav className="w-52 shrink-0 bg-[#161e2d] border-r border-[#414750] overflow-y-auto py-2">
          {SERVICES.map((s) => (
            <button key={s.id} onClick={() => goView(s.id)} data-testid={`aws-nav-${s.id}`}
              className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${view === s.id && !detail ? "bg-[#ff9900]/15 text-[#ff9900] border-l-2 border-[#ff9900]" : "text-slate-300 hover:bg-white/5 border-l-2 border-transparent"}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </nav>
        {/* main */}
        <main className="flex-1 overflow-y-auto" onClick={() => { setServicesOpen(false); setAcctOpen(false); }}>
          {mainContent}
        </main>
      </div>

      {wizard && <LaunchWizard onClose={() => setWizard(false)} onLaunched={(id) => { setWizard(false); if (id) openInstance(id); }} />}
      {connectInst && <Ec2Terminal instance={connectInst} onClose={() => setConnectInst(null)} />}
      <AwsGuidedPanel open={guided} initialScenario={initialScenario} onClose={() => { setGuided(false); setInitialScenario(null); }} setView={goView} />
    </div>
  );
}

export default function AwsConsole() {
  return (
    <AwsProvider>
      <ConsoleShell />
    </AwsProvider>
  );
}
